/*
routes.js
Görev: Uygulamanızdaki sayfa yönlendirmelerini (routing) tanımlamak için kullanılır.
İşlev: Şu anda boş, 
ancak React Router DOM kullanarak farklı sayfalar 
(Ana Sayfa, Bloglar, İletişim vb.) arasında geçiş yapabilirsiniz.
Öneri: Kullanıcı site ve admin paneli için ayrı rotalar tanımlayabilirsiniz. Örneğin:

*/
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Meta from './components/SEO/Meta'; // Meta bileşenini import ettiğinizden emin olun

// Genel Sayfalar ve Bileşenler
// Ana sayfa ilk açılan ekran olduğu için doğrudan yükleniyor; diğer sayfalar
// lazy, böylece ziyaretçi girmediği sayfaların (ve tüm admin panelinin)
// kodunu indirmiyor.
import Home from './pages/Home';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import DynamicBackground from './components/DynamicBackground';
import CookieConsent from './components/CookieConsent';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/Admin/ProtectedRoute';
import { loadAnalytics, trackPageView } from './utils/analytics';

const Blogs = lazy(() => import('./pages/Blogs'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const BeOurPartner = lazy(() => import('./pages/BeOurPartner'));
const BlogPostDetail = lazy(() => import('./components/Blog/BlogPostDetail'));
const ServiceDetail = lazy(() => import('./components/Services/ServiceDetail'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));

// Admin Sayfaları
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminPartners = lazy(() => import('./pages/admin/AdminPartners'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminApplyProject = lazy(() => import('./pages/admin/AdminApplyProject'));

const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Yeni, daha sofistike ve uyumlu gradyan renk paleti
const colors = [
  // Her dizi bir gradyanı temsil eder: [başlangıç rengi, bitiş rengi]
  ['#eaf2f8', '#e8eaf6'], // Hero: Sakin ve profesyonel başlangıç
  ['#a1c4fd', '#c2e9fb'], // Services: Gökyüzü mavisinden, pudra mavisine
  ['#b7ffea', '#7fd8ff'], // Blog: Açık turkuazdan, canlı maviye (daha modern ve ferah)
  ['#ffe29f', '#ffa99f'], // Testimonials: Yumuşak sarıdan, pastel mercana (daha sıcak ve samimi)
  ['#e0c3fc', '#8ec5fc'], // Contact: Açık lavantadan, yumuşak maviye (daha uyumlu ve teşvik edici)
];

// Bu yardımcı bileşen, admin sayfalarında Navbar, Footer ve arkaplanı gizler.
const LayoutManager = () => {
  const location = useLocation();
  const [bgColor, setBgColor] = useState(colors[0]);
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Onay zaten verilmişse scripti yükle. Onay yoksa hiçbir şey yapmaz.
  useEffect(() => {
    loadAnalytics();
  }, []);

  // SPA düzeltmesi: React Router gezinmeleri sayfayı yeniden yüklemediği için
  // gtag('config') bir daha çalışmıyordu; açılış sayfası dışında hiçbir
  // görüntüleme kaydedilmiyordu.
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Redux store'dan admin bilgilerini çekiyoruz
  const { info: adminInfo } = useSelector((state) => state.adminInfo);

  // YENİ: KONTROL İÇİN CONSOLE.LOG
  // Bu log, sayfa her yüklendiğinde terminalde veya tarayıcı konsolunda görünmelidir.
  console.log("LayoutManager'daki Logo URL:", adminInfo?.logoimageUrl);

  return (
    <>
      {/* DEĞİŞİKLİK: Favicon'u 'logoimageUrl' alanından alıyoruz */}
      <Meta favicon={adminInfo?.logoimageUrl} />

      {!isAdminRoute && <DynamicBackground color={bgColor} />}
      {!isAdminRoute && <Navbar />}
      <main>
        <Suspense fallback={<LoadingSpinner text="Loading..." />}>
        <Routes>
          {/* === GENEL (PUBLIC) ROTALAR === */}
          <Route path="/" element={<Home setBgColor={setBgColor} colors={colors} />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogPostDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/be-our-partner" element={<BeOurPartner />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* === ADMIN ROTALARI === */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="services" element={<AdminServices />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="apply-project" element={<AdminApplyProject />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>

          {/* YENİ: 404 Sayfası için "Catch-All" Route */}
          {/* Bu route her zaman en sonda olmalıdır! */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <CookieConsent />}
    </>
  );
};

export default LayoutManager;


